import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import {
  FuelConnection,
  FuelConsumptionReportRow,
  FuelDashboard,
  FuelProviderApp,
  FuelSyncRun,
  FuelTransaction,
  FuelTransactionFilters
} from './fuel-management.model';
import { FuelManagementService } from './fuel-management.service';

type FuelPage = 'overview' | 'marketplace' | 'connections' | 'transactions' | 'reports';
type FuelProviderDefaults = Partial<{
  baseUrl: string;
  apiKeyHeader: string;
  wsVersion: string;
  httpMethod: string;
  transactionsPath: string;
  testPath: string;
  headersJson: string;
  requestBody: string;
  notes: string;
}>;

@Component({
  selector: 'app-fuel-management-workspace',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    FileUploadModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageShellComponent
  ],
  templateUrl: './fuel-management-workspace.component.html',
  styleUrl: './fuel-management-workspace.component.css',
  providers: [MessageService]
})
export class FuelManagementWorkspaceComponent implements OnInit, OnDestroy {
  @ViewChild('fuelCsvUploader') fuelCsvUploader?: FileUpload;

  private readonly fuelService = inject(FuelManagementService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly destroy$ = new Subject<void>();

  activePage: FuelPage = 'overview';
  loading = false;
  saving = false;
  importLoading = false;
  transactionLoading = false;
  connectionDialogVisible = false;

  providers: FuelProviderApp[] = [];
  providerOptions: { label: string; value: string }[] = [];
  connectionProviderOptions: { label: string; value: string }[] = [];
  importProviderOptions: { label: string; value: string }[] = [];
  connections: FuelConnection[] = [];
  transactions: FuelTransaction[] = [];
  reportRows: FuelConsumptionReportRow[] = [];
  runs: FuelSyncRun[] = [];
  dashboard: FuelDashboard = {};
  totalTransactions = 0;

  selectedImportConnectionId: number | null = null;
  selectedImportProviderCode = 'GENERIC_CSV';
  selectedFile: File | null = null;

  transactionFilters: FuelTransactionFilters = {
    page: 0,
    pageSize: 50
  };

  readonly navItems = [
    { label: 'Overview', icon: 'fa-solid fa-chart-line', route: '/fuel-management', page: 'overview' },
    { label: 'Marketplace', icon: 'fa-solid fa-store', route: '/fuel-management/marketplace', page: 'marketplace' },
    { label: 'Connections', icon: 'fa-solid fa-plug', route: '/fuel-management/connections', page: 'connections' },
    { label: 'Transactions', icon: 'fa-solid fa-gas-pump', route: '/fuel-management/transactions', page: 'transactions' },
    { label: 'Reports', icon: 'fa-solid fa-chart-column', route: '/fuel-management/reports', page: 'reports' }
  ];

  readonly reconciliationOptions = [
    { label: 'All', value: '' },
    { label: 'Matched', value: 'MATCHED' },
    { label: 'Unmatched', value: 'UNMATCHED' },
    { label: 'Ignored', value: 'IGNORED' }
  ];
  readonly httpMethodOptions = ['GET', 'POST'].map(method => ({ label: method, value: method }));
  readonly providerBaseUrls: Record<string, string> = {
    PETROAPP: environment.fuelProviderBaseUrls?.PETROAPP || 'https://app-public.staging.petroapp.app/webservice/',
    ALDREES_WAIE: environment.fuelProviderBaseUrls?.ALDREES_WAIE || 'https://devint.aldrees.com/api/',
    ARAMCO_FUEL_CARD: 'https://www.aramcostation.com/',
    DARB_PAY: 'https://www.darbpay.com/',
    GOREX: 'https://www.gorex.ai/',
    SASCO_CONTROL_CARD: 'https://sasco.com.sa/',
    FLEETOO: 'https://fleetoo.sa/'
  };
  readonly providerDefaults: Record<string, FuelProviderDefaults> = {
    PETROAPP: {
      baseUrl: this.providerBaseUrls['PETROAPP'],
      apiKeyHeader: '',
      wsVersion: 'v2.0',
      httpMethod: 'GET',
      transactionsPath: 'bills?page=1&lang=en',
      testPath: 'vehicles?page=1',
      notes: 'Bills API supports optional from=YYYY-MM-DD, to=YYYY-MM-DD, branch_name, and lang=en query parameters.'
    },
    ALDREES_WAIE: {
      baseUrl: this.providerBaseUrls['ALDREES_WAIE'],
      apiKeyHeader: '',
      httpMethod: 'POST',
      transactionsPath: 'REPORTSUBMIT',
      testPath: 'REPORTLOADPRODUCTTYPE',
      notes: 'REPORTSUBMIT uses AU_REPORTS_QUERY. REPORTTYPE 0 = monthly transaction statement, 1 = FleetWiseFuelUsage, 2 = FuelConsumptionByCustomer, 6 = monthly consumption invoice, 8 = this year consumption, 9 = WAIE high consumption. Public docs do not define auth headers or response samples.'
    }
  };

  readonly connectionForm = this.fb.group({
    connectionId: [null as number | null],
    providerCode: ['', Validators.required],
    displayName: ['', Validators.required],
    baseUrl: [''],
    active: [true],
    defaultConnection: [false],
    wsSk: [''],
    apiKey: [''],
    apiKeyHeader: ['Api-Key'],
    bearerToken: [''],
    subscriptionKey: [''],
    wsVersion: ['1.0'],
    httpMethod: ['GET'],
    transactionsPath: [''],
    testPath: [''],
    headersJson: [''],
    requestBody: [''],
    notes: ['']
  });

  ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.activePage = (data['page'] || 'overview') as FuelPage;
      this.loadAll();
    });
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const importProvider = params.get('importProvider');
      if (importProvider) {
        this.selectedImportProviderCode = importProvider;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Fuel Spend', value: this.money(this.dashboard.totalAmount), hint: 'Selected period', tone: 'info' },
      { label: 'Liters', value: this.number(this.dashboard.totalQuantity, 2), tone: 'success' },
      { label: 'Avg Unit Price', value: this.money(this.dashboard.averageUnitPrice), hint: 'Per liter' },
      { label: 'Transactions', value: this.number(this.dashboard.transactionCount, 0) },
      { label: 'Unmatched', value: this.number(this.dashboard.unmatchedCount, 0), tone: (this.dashboard.unmatchedCount || 0) > 0 ? 'warn' : 'success' }
    ];
  }

  get connectionOptions(): { label: string; value: number }[] {
    return this.connections
      .filter(connection => !!connection.connectionId)
      .map(connection => ({
        label: `${connection.displayName} (${connection.providerCode})`,
        value: connection.connectionId!
      }));
  }

  get effectiveImportProviderCode(): string {
    return this.connections.find(connection => connection.connectionId === this.selectedImportConnectionId)?.providerCode
      || this.selectedImportProviderCode;
  }

  get isAldreesImport(): boolean {
    return this.effectiveImportProviderCode === 'ALDREES_WAIE';
  }

  get importChooseLabel(): string {
    return this.isAldreesImport ? 'Select Report' : 'Select CSV';
  }

  get importActionLabel(): string {
    return this.isAldreesImport ? 'Import Report' : 'Import CSV';
  }

  get importAccept(): string {
    return this.isAldreesImport
      ? '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv'
      : '.csv,text/csv';
  }

  get importHelpText(): string {
    return this.isAldreesImport
      ? 'Upload Aldrees WAIE Excel transaction statement (.xlsx).'
      : 'Upload a canonical fuel CSV file.';
  }

  loadAll(): void {
    this.loading = true;
    forkJoin({
      providers: this.fuelService.marketplaceApps(),
      connections: this.fuelService.connections(),
      dashboard: this.fuelService.dashboard(),
      transactions: this.fuelService.transactions(this.transactionFilters),
      reportRows: this.fuelService.consumptionReport(),
      runs: this.fuelService.runs()
    }).pipe(finalize(() => this.loading = false))
      .subscribe({
        next: response => {
          this.providers = response.providers;
          this.providerOptions = response.providers.map(provider => ({
            label: provider.displayName,
            value: provider.providerCode
          }));
          this.connectionProviderOptions = response.providers
            .filter(provider => this.canConnectProvider(provider))
            .map(provider => ({
              label: provider.displayName,
              value: provider.providerCode
            }));
          this.importProviderOptions = response.providers
            .filter(provider => this.canImportProvider(provider))
            .map(provider => ({
              label: provider.displayName,
              value: provider.providerCode
            }));
          if (!this.importProviderOptions.some(option => option.value === this.selectedImportProviderCode)) {
            this.selectedImportProviderCode = this.importProviderOptions[0]?.value || 'GENERIC_CSV';
          }
          this.connections = response.connections;
          this.dashboard = response.dashboard || {};
          this.transactions = response.transactions.items || [];
          this.totalTransactions = response.transactions.count || this.transactions.length;
          this.reportRows = response.reportRows;
          this.runs = response.runs;
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary: 'Fuel data failed',
          detail: 'Unable to load fuel management data.'
        })
      });
  }

  loadTransactions(page = this.transactionFilters.page || 0): void {
    this.transactionFilters.page = page;
    this.transactionLoading = true;
    this.fuelService.transactions(this.transactionFilters)
      .pipe(finalize(() => this.transactionLoading = false))
      .subscribe({
        next: response => {
          this.transactions = response.items || [];
          this.totalTransactions = response.count || this.transactions.length;
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Search failed', detail: 'Unable to load fuel transactions.' })
      });
  }

  onTransactionLazy(event: any): void {
    const rows = event.rows || 50;
    const first = event.first || 0;
    this.transactionFilters.pageSize = rows;
    this.loadTransactions(Math.floor(first / rows));
  }

  openConnection(provider?: FuelProviderApp, connection?: FuelConnection): void {
    if (provider && !this.canConnectProvider(provider)) {
      if (this.canImportProvider(provider)) {
        this.openCsvImport(provider);
        return;
      }
      this.messageService.add({
        severity: 'info',
        summary: 'No connection needed',
        detail: `${provider.displayName} does not require API credentials.`
      });
      return;
    }
    const configuration = connection?.configuration || {};
    const providerCode = connection?.providerCode || provider?.providerCode || '';
    const defaults = this.defaultProviderConfig(providerCode);
    this.connectionForm.reset({
      connectionId: connection?.connectionId || null,
      providerCode,
      displayName: connection?.displayName || provider?.displayName || '',
      baseUrl: connection?.baseUrl || this.defaultBaseUrl(providerCode),
      active: connection?.active ?? true,
      defaultConnection: connection?.defaultConnection ?? false,
      wsSk: '',
      apiKey: '',
      apiKeyHeader: configuration['apiKeyHeader'] || configuration['apiKeyHeaderName'] || (defaults.apiKeyHeader ?? 'Api-Key'),
      bearerToken: '',
      subscriptionKey: '',
      wsVersion: configuration['wsVersion'] || configuration['WS-Version'] || defaults.wsVersion || '1.0',
      httpMethod: configuration['httpMethod'] || configuration['syncHttpMethod'] || defaults.httpMethod || 'GET',
      transactionsPath: configuration['transactionsPath'] || defaults.transactionsPath || '',
      testPath: configuration['testPath'] || defaults.testPath || '',
      headersJson: configuration['headersJson'] || configuration['customHeadersJson'] || defaults.headersJson || '',
      requestBody: configuration['requestBody'] || configuration['syncRequestBody'] || defaults.requestBody || '',
      notes: configuration['notes'] || defaults.notes || ''
    });
    this.connectionDialogVisible = true;
  }

  openCsvImport(provider?: FuelProviderApp): void {
    if (provider?.providerCode) {
      this.selectedImportProviderCode = provider.providerCode;
    }
    this.connectionDialogVisible = false;
    this.router.navigate(['/fuel-management/connections'], {
      queryParams: { importProvider: this.selectedImportProviderCode }
    });
  }

  onImportTargetChanged(): void {
    this.selectedFile = null;
    this.fuelCsvUploader?.clear();
  }

  onProviderChanged(providerCode: string): void {
    const provider = this.providers.find(item => item.providerCode === providerCode);
    const defaults = this.defaultProviderConfig(providerCode);
    const currentBaseUrl = this.connectionForm.controls.baseUrl.value || '';
    const shouldReplaceBaseUrl = !currentBaseUrl || this.isKnownProviderBaseUrl(currentBaseUrl);
    this.connectionForm.patchValue({
      displayName: provider?.displayName || this.connectionForm.controls.displayName.value || '',
      baseUrl: shouldReplaceBaseUrl ? this.defaultBaseUrl(providerCode) : currentBaseUrl,
      apiKeyHeader: this.defaultFormValue('apiKeyHeader', defaults.apiKeyHeader ?? 'Api-Key', ['Api-Key', ...this.providerDefaultValues('apiKeyHeader')]),
      wsVersion: this.defaultFormValue('wsVersion', defaults.wsVersion || '1.0', ['1.0', ...this.providerDefaultValues('wsVersion')]),
      httpMethod: this.defaultFormValue('httpMethod', defaults.httpMethod || 'GET', ['GET', ...this.providerDefaultValues('httpMethod')]),
      transactionsPath: this.defaultFormValue('transactionsPath', defaults.transactionsPath || '', this.providerDefaultValues('transactionsPath')),
      testPath: this.defaultFormValue('testPath', defaults.testPath || '', this.providerDefaultValues('testPath')),
      headersJson: this.defaultFormValue('headersJson', defaults.headersJson || '', this.providerDefaultValues('headersJson')),
      requestBody: this.defaultFormValue('requestBody', defaults.requestBody || '', this.providerDefaultValues('requestBody')),
      notes: this.defaultFormValue('notes', defaults.notes || '', this.providerDefaultValues('notes'))
    }, { emitEvent: false });
  }

  saveConnection(): void {
    if (this.connectionForm.invalid) {
      this.connectionForm.markAllAsTouched();
      return;
    }
    const value = this.connectionForm.getRawValue();
    const credentials: Record<string, any> = {};
    const configuration: Record<string, any> = {};
    this.putIfPresent(credentials, 'wsSk', value.wsSk);
    this.putIfPresent(credentials, 'apiKey', value.apiKey);
    this.putIfPresent(credentials, 'bearerToken', value.bearerToken);
    this.putIfPresent(credentials, 'subscriptionKey', value.subscriptionKey);
    this.putIfPresent(configuration, 'apiKeyHeader', value.apiKeyHeader);
    this.putIfPresent(configuration, 'wsVersion', value.wsVersion);
    this.putIfPresent(configuration, 'httpMethod', value.httpMethod);
    this.putIfPresent(configuration, 'transactionsPath', value.transactionsPath);
    this.putIfPresent(configuration, 'testPath', value.testPath);
    this.putIfPresent(configuration, 'headersJson', value.headersJson);
    this.putIfPresent(configuration, 'requestBody', value.requestBody);
    this.putIfPresent(configuration, 'notes', value.notes);

    const payload: FuelConnection = {
      connectionId: value.connectionId || undefined,
      providerCode: value.providerCode || '',
      displayName: value.displayName || '',
      baseUrl: value.baseUrl || undefined,
      active: !!value.active,
      defaultConnection: !!value.defaultConnection,
      credentials,
      configuration
    };

    this.saving = true;
    this.fuelService.saveConnection(payload)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: () => {
          this.connectionDialogVisible = false;
          this.messageService.add({ severity: 'success', summary: 'Connection saved', detail: 'Fuel connection settings were saved.' });
          this.loadAll();
        },
        error: error => this.messageService.add({
          severity: 'error',
          summary: 'Save failed',
          detail: this.apiErrorDetail(error, 'Unable to save fuel connection.')
        })
      });
  }

  testConnection(connection: FuelConnection): void {
    if (!connection.connectionId) return;
    this.fuelService.testConnection(connection.connectionId).subscribe({
      next: run => {
        this.messageService.add({ severity: this.isFailed(run.status) ? 'warn' : 'success', summary: 'Test completed', detail: run.errorMessage || connection.lastTestMessage || run.status || 'Completed' });
        this.loadAll();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Test failed', detail: 'Fuel service did not complete the connection test.' })
    });
  }

  syncConnection(connection: FuelConnection): void {
    if (!connection.connectionId) return;
    this.fuelService.syncConnection(connection.connectionId).subscribe({
      next: run => {
        this.messageService.add({ severity: this.isFailed(run.status) ? 'warn' : 'success', summary: 'Sync completed', detail: run.errorMessage || `${run.acceptedCount || 0} accepted, ${run.duplicateCount || 0} duplicates.` });
        this.loadAll();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Sync failed', detail: 'Fuel service did not complete the sync.' })
    });
  }

  onFileSelected(event: FileSelectEvent | Event): void {
    const primeEvent = event as FileSelectEvent;
    const inputEvent = event as Event;
    const input = inputEvent.target instanceof HTMLInputElement ? inputEvent.target : null;
    this.selectedFile = primeEvent.files?.[0] ?? input?.files?.item(0) ?? null;
  }

  importCsv(): void {
    if (!this.selectedFile) {
      this.messageService.add({ severity: 'warn', summary: 'File required', detail: `Choose a ${this.isAldreesImport ? 'report' : 'CSV'} file first.` });
      return;
    }
    if (!this.isSelectedImportFileAllowed(this.selectedFile)) {
      this.messageService.add({ severity: 'warn', summary: 'Unsupported file', detail: this.isAldreesImport ? 'Upload an Aldrees Excel report (.xlsx or .xls).' : 'Upload a CSV file.' });
      return;
    }
    const selectedConnection = this.connections.find(connection => connection.connectionId === this.selectedImportConnectionId);
    const providerCode = selectedConnection?.providerCode || this.selectedImportProviderCode;
    const request = this.isAldreesImport
      ? this.fuelService.importReport(this.selectedFile, this.selectedImportConnectionId, providerCode)
      : this.fuelService.importCsv(this.selectedFile, this.selectedImportConnectionId, providerCode);
    this.importLoading = true;
    request
      .pipe(finalize(() => this.importLoading = false))
      .subscribe({
        next: run => {
          this.messageService.add({ severity: this.isFailed(run.status) ? 'warn' : 'success', summary: 'Import completed', detail: run.errorMessage || `${run.acceptedCount || 0} accepted, ${run.failedCount || 0} failed.` });
          this.selectedFile = null;
          this.fuelCsvUploader?.clear();
          this.loadAll();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Import failed', detail: 'Unable to import the selected fuel report.' })
      });
  }

  reconcile(transaction: FuelTransaction): void {
    if (!transaction.transactionId) return;
    const vehiclePlateNumber = window.prompt('Vehicle plate number', transaction.vehiclePlateNumber || '');
    if (vehiclePlateNumber === null) return;
    this.fuelService.reconcile(transaction.transactionId, { vehiclePlateNumber: vehiclePlateNumber.trim() }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Transaction matched', detail: 'Fuel transaction was reconciled.' });
        this.loadAll();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Reconcile failed', detail: 'Unable to reconcile transaction.' })
    });
  }

  ignore(transaction: FuelTransaction): void {
    if (!transaction.transactionId) return;
    const reason = window.prompt('Ignore reason', transaction.ignoredReason || '');
    if (reason === null) return;
    this.fuelService.ignore(transaction.transactionId, reason.trim()).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Transaction ignored', detail: 'Fuel transaction was marked ignored.' });
        this.loadAll();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Unable to ignore transaction.' })
    });
  }

  providerName(providerCode?: string): string {
    return this.providers.find(provider => provider.providerCode === providerCode)?.displayName || providerCode || '-';
  }

  statusSeverity(status?: string): any {
    const normalized = (status || '').toUpperCase();
    if (['ACTIVE', 'COMPLETED', 'MATCHED', 'POSTED'].includes(normalized)) return 'success';
    if (['RUNNING', 'PENDING', 'UNMATCHED'].includes(normalized)) return 'warn';
    if (['FAILED', 'ERROR'].includes(normalized)) return 'danger';
    if (['IGNORED', 'INACTIVE'].includes(normalized)) return 'secondary';
    return 'info';
  }

  canConnectProvider(provider?: FuelProviderApp | null): boolean {
    return !!provider?.supportsApiSync && !this.isManualProvider(provider);
  }

  canImportProvider(provider?: FuelProviderApp | null): boolean {
    return !!provider?.supportsCsvImport && !this.isManualProvider(provider);
  }

  importTagValue(provider?: FuelProviderApp | null): string {
    return provider?.providerCode === 'ALDREES_WAIE' ? 'Report Import' : 'CSV Import';
  }

  importButtonLabel(provider?: FuelProviderApp | null): string {
    return provider?.providerCode === 'ALDREES_WAIE' ? 'Import Report' : 'Import CSV';
  }

  isManualProvider(provider?: FuelProviderApp | null): boolean {
    return !!provider?.supportsManualEntry || provider?.providerCode === 'MANUAL_ENTRY';
  }

  money(value?: number | null, currency = 'SAR'): string {
    return `${this.number(value, 2)} ${currency}`;
  }

  number(value?: number | null, digits = 0): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value || 0);
  }

  private putIfPresent(target: Record<string, any>, key: string, value: any): void {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      target[key] = value;
    }
  }

  private defaultBaseUrl(providerCode?: string | null): string {
    return providerCode ? this.providerBaseUrls[providerCode] || '' : '';
  }

  private defaultProviderConfig(providerCode?: string | null): FuelProviderDefaults {
    const defaults = providerCode ? this.providerDefaults[providerCode] || {} : {};
    if (providerCode === 'ALDREES_WAIE') {
      return {
        ...defaults,
        requestBody: this.defaultAldreesReportBody()
      };
    }
    return defaults;
  }

  private defaultFormValue(controlName: keyof typeof this.connectionForm.controls, fallback: string, knownDefaults: string[] = []): string {
    const current = this.connectionForm.controls[controlName].value;
    if (current === null || current === undefined || String(current).trim() === '') {
      return fallback;
    }
    return knownDefaults.includes(String(current)) ? fallback : String(current);
  }

  private providerDefaultValues(key: keyof FuelProviderDefaults): string[] {
    const values = Object.values(this.providerDefaults)
      .map(defaults => defaults[key])
      .filter((value): value is string => typeof value === 'string' && value.trim() !== '');
    if (key === 'requestBody') {
      values.push(this.defaultAldreesReportBody());
    }
    return values;
  }

  private isKnownProviderBaseUrl(value: string): boolean {
    return Object.values(this.providerBaseUrls).includes(value)
      || value === 'https://app-public.staging.petroapp.app/webservice/'
      || value === 'https://service.petroapp.com.sa/'
      || value === 'https://service.petroapp.com.sa/webservice/'
      || value === 'https://devint.aldrees.com/'
      || value === 'https://devint.aldrees.com/api/'
      || value === 'https://int.aldrees.com/api/';
  }

  private isFailed(status?: string): boolean {
    return (status || '').toUpperCase() === 'FAILED';
  }

  private isSelectedImportFileAllowed(file: File): boolean {
    const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';
    return this.isAldreesImport
      ? ['xlsx', 'xls', 'csv'].includes(extension || '')
      : extension === 'csv';
  }

  private apiErrorDetail(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.error
      || error?.message
      || fallback;
  }

  private defaultAldreesReportBody(): string {
    const today = new Date();
    const periodFrom = this.formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const periodTo = this.formatLocalDate(today);
    return JSON.stringify({
      REPORTTYPE: 2,
      ISSUMMARY: '',
      CUSTID: 'YOUR_CUSTOMER_ID',
      PERIODFROM: periodFrom,
      PERIODTO: periodTo,
      GROUPBYPLATE: '',
      CONNECTIONSTATUS: '',
      LIST_PLATENO: '',
      LIST_DIVISION: '',
      LIST_BRANCH: '',
      LIST_DEPARTMENT: '',
      LIST_STATION: '',
      LIST_DRIVER: '',
      LIST_FUELTYPE: '',
      LIST_SERVICETYPE: '',
      LIST_VEHICLETYPE: '',
      LIST_PLACES: '',
      SHIFT_CLOSING: '',
      STATION_SORT_BY: '',
      EMAILID: ''
    }, null, 2);
  }

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
